import { Spin, Tag, Typography } from 'antd';
import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  type FormSection,
  type WorkflowField,
  type WorkflowStep,
  type WorkflowTemplate,
  workflowsApi,
} from '@/api/workflows';
import { PageHeader } from '@/components/PageHeader';
import { handleApiError } from '@/lib/handleApiError';

import { describeCondition } from './conditionSummary';
import { fieldDisplayLabelKey, metaForFieldType } from './form-builder/fieldMeta';
import { ReadOnlyFieldProperties } from './ReadOnlyFieldProperties';

const { Text } = Typography;

const CANVAS: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflowY: 'auto',
  background: '#f5f7fa',
  padding: 20,
};

const RAIL: CSSProperties = {
  width: 360,
  flexShrink: 0,
  borderLeft: '1px solid #e5e7eb',
  background: '#fff',
  padding: 18,
  overflowY: 'auto',
};

const SECTION_CARD: CSSProperties = {
  background: '#fff',
  borderRadius: 14,
  border: '1px solid #e5e7eb',
  marginBottom: 18,
  overflow: 'hidden',
};

const SECTION_HEADER: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
  background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
  borderBottom: '1px solid #e5e7eb',
  padding: '12px 16px',
  cursor: 'pointer',
};

const COLUMN_SLOT: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '10px 12px',
  background: '#fff',
  minHeight: 52,
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'border-color 140ms ease, box-shadow 140ms ease',
};

type Selection =
  { kind: 'field'; field: WorkflowField } | { kind: 'section'; section: FormSection } | null;

/**
 * Read-only, builder-style page showing one DynamicForm step's full authoring
 * configuration. Recreates the originator form-builder's canvas + properties
 * rail (minus the palette / all edit affordances): the canvas lists sections
 * and field chips; clicking a field (or a section header) shows its properties
 * on the right. Reached per-step from the review / version surfaces via
 * {@link WorkflowReviewBody}. When `?version=N` is present it renders that
 * version's FROZEN step so an archived version never leaks current state.
 */
export function StepConfigPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id, stepId } = useParams<{ id: string; stepId: string }>();
  const [searchParams] = useSearchParams();
  const isFr = i18n.language.startsWith('fr');

  const versionParam = searchParams.get('version');
  const requestedVersion = versionParam !== null ? Number(versionParam) : NaN;

  const [step, setStep] = useState<WorkflowStep | null>(null);
  const [allSteps, setAllSteps] = useState<WorkflowStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState<Selection>(null);

  useEffect(() => {
    if (!id || !stepId) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setSelected(null);
    (async () => {
      try {
        // When a version is pinned, resolve it to its frozen step list; a
        // non-numeric ?version can never match, so treat it as not-found.
        const wantVersion = versionParam !== null;
        const versionResolvable = wantVersion && Number.isFinite(requestedVersion);
        // Fetched directly: the versions list carries no step snapshots, and the frozen
        // step list is the only thing this page wants from a version.
        const [fresh, pinned] = await Promise.all([
          workflowsApi.get(id),
          versionResolvable
            ? workflowsApi.getVersion(id, requestedVersion).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        let sourceSteps: WorkflowStep[] | null;
        if (wantVersion) {
          sourceSteps = pinned ? pinned.steps : null;
        } else {
          sourceSteps = (fresh as WorkflowTemplate).steps ?? [];
        }
        const found = sourceSteps?.find((s) => s.id === stepId) ?? null;
        if (!sourceSteps || !found) {
          setNotFound(true);
          return;
        }
        setAllSteps(sourceSteps);
        setStep(found);
      } catch (err) {
        if (!cancelled) handleApiError(err, t, t('workflow.details.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, stepId, versionParam, requestedVersion, t]);

  const sections = useMemo(() => step?.sections ?? [], [step]);
  const stepTitle = step
    ? (isFr ? step.titleFr : step.titleEn) || step.titleEn || step.titleFr
    : '';

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }
  if (notFound || !step) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="secondary">{t('workflow.versions.detailsNotFound')}</Text>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 600 }}>
      <PageHeader
        title={`${stepTitle}: ${t('workflow.review.config.subtitle')}`}
        onBack={() => navigate(-1)}
      />

      <div
        style={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        {/* CANVAS — sections + field chips (read-only, click to inspect) */}
        <section style={CANVAS}>
          {sections.length === 0 ? (
            <Text type="secondary">{t('workflow.review.config.noSections')}</Text>
          ) : (
            sections.map((section) => (
              <SectionCard
                key={section.id}
                section={section}
                isFr={isFr}
                selectedFieldId={selected?.kind === 'field' ? selected.field.id : null}
                sectionSelected={selected?.kind === 'section' && selected.section.id === section.id}
                onSelectSection={() => setSelected({ kind: 'section', section })}
                onSelectField={(field) => setSelected({ kind: 'field', field })}
              />
            ))
          )}
        </section>

        {/* PROPERTIES RAIL — selected field / section, or a hint */}
        <aside style={RAIL}>
          {selected?.kind === 'field' ? (
            <ReadOnlyFieldProperties field={selected.field} allSteps={allSteps} isFr={isFr} />
          ) : selected?.kind === 'section' ? (
            <SectionProperties section={selected.section} allSteps={allSteps} isFr={isFr} />
          ) : (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('workflow.review.config.selectFieldHint')}
            </Text>
          )}
        </aside>
      </div>
    </div>
  );
}

function SectionCard({
  section,
  isFr,
  selectedFieldId,
  sectionSelected,
  onSelectSection,
  onSelectField,
}: {
  section: FormSection;
  isFr: boolean;
  selectedFieldId: string | null;
  sectionSelected: boolean;
  onSelectSection: () => void;
  onSelectField: (field: WorkflowField) => void;
}) {
  const { t } = useTranslation();
  const title =
    (isFr ? section.titleFr : section.titleEn) ||
    section.titleEn ||
    t('workflow.builder.section.untitled');

  return (
    <div
      style={
        sectionSelected
          ? { ...SECTION_CARD, borderColor: '#0179C1', boxShadow: '0 0 0 1px #0179C1' }
          : SECTION_CARD
      }
    >
      <div
        style={SECTION_HEADER}
        onClick={onSelectSection}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onSelectSection();
        }}
      >
        <Text strong style={{ fontSize: 15, color: '#111827', flex: 1, minWidth: 0 }}>
          {title}
        </Text>
        {section.repeatable && (
          <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
            {t('workflow.review.config.repeatable', {
              min: section.minInstances ?? 1,
              max: section.maxInstances ?? '∞',
            })}
          </Tag>
        )}
        {(section.conditions?.length ?? 0) > 0 && (
          <Tag color="blue" style={{ marginInlineEnd: 0 }}>
            {t('approver.review.conditionsPeekChip', {
              defaultValue: '{{count}} rule',
              count: section.conditions.length,
              defaultValue_other: '{{count}} rules',
            })}
          </Tag>
        )}
      </div>

      <div style={{ padding: 16 }}>
        {section.rows.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t('workflow.builder.section.empty')}
          </Text>
        ) : (
          section.rows.map((row) => (
            <div
              key={row.id}
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${row.columns.length || 1}, minmax(0, 1fr))`,
                gap: 12,
                marginBottom: 12,
              }}
            >
              {row.columns.map((col) =>
                col.field ? (
                  <FieldChip
                    key={col.id}
                    field={col.field}
                    isFr={isFr}
                    selected={col.field.id === selectedFieldId}
                    onSelect={() => onSelectField(col.field!)}
                  />
                ) : (
                  <div
                    key={col.id}
                    style={{ ...COLUMN_SLOT, cursor: 'default', borderStyle: 'dashed' }}
                  />
                ),
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function FieldChip({
  field,
  isFr,
  selected,
  onSelect,
}: {
  field: WorkflowField;
  isFr: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const meta = metaForFieldType(field.type);
  const label =
    (isFr ? field.labelFr : field.labelEn) || field.labelEn || t(fieldDisplayLabelKey(field));
  return (
    <div
      style={
        selected
          ? { ...COLUMN_SLOT, borderColor: '#0179C1', boxShadow: '0 0 0 1px #0179C1' }
          : COLUMN_SLOT
      }
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect();
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minWidth: 0 }}>
        <Text
          strong
          style={{
            fontSize: 13,
            flex: '0 1 auto',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Text>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            lineHeight: 1,
            color: meta.tintFg,
            background: meta.tintBg,
            padding: '3px 8px',
            borderRadius: 999,
            flexShrink: 0,
          }}
        >
          <span style={{ display: 'inline-flex' }}>{meta.icon()}</span>
          <span>{t(fieldDisplayLabelKey(field))}</span>
        </span>
      </div>
    </div>
  );
}

function SectionProperties({
  section,
  allSteps,
  isFr,
}: {
  section: FormSection;
  allSteps: WorkflowStep[];
  isFr: boolean;
}) {
  const { t } = useTranslation();
  const sp = (key: string) => t(`workflow.builder.sectionProperties.${key}`);
  const conditions = section.conditions ?? [];

  const rows: Array<{ label: string; value: React.ReactNode }> = [];
  rows.push({ label: sp('nameEn'), value: section.titleEn || '—' });
  if (section.titleFr) rows.push({ label: sp('nameFr'), value: section.titleFr });
  rows.push({
    label: sp('repeatable'),
    value: section.repeatable ? t('common.yes') : t('common.no'),
  });
  if (section.repeatable) {
    rows.push({ label: sp('minInstances'), value: section.minInstances ?? 1 });
    rows.push({ label: sp('maxInstances'), value: section.maxInstances ?? '∞' });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Text strong style={{ fontSize: 14 }}>
        {t('workflow.builder.sectionProperties.title')}
      </Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.label}
            </Text>
            <div style={{ fontSize: 13, color: '#111827', wordBreak: 'break-word' }}>{r.value}</div>
          </div>
        ))}
      </div>
      {conditions.length > 0 && (
        <div style={{ borderTop: '1px dashed #eef2f7', paddingTop: 8 }}>
          <Text
            type="secondary"
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              display: 'block',
              marginBottom: 6,
            }}
          >
            {t('approver.review.conditionsPeekTitle', { defaultValue: 'Visibility conditions' })}
          </Text>
          <ul style={{ margin: 0, paddingInlineStart: 18, display: 'grid', gap: 2 }}>
            {conditions.map((c, i) => (
              <li key={i}>
                <Text style={{ fontSize: 13 }}>{describeCondition(c, allSteps, isFr)}</Text>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
